import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, apiError, eur, openPdf } from "@/pages/rc/rcApi";
import OutcomeBadge from "@/pages/rc/OutcomeBadge";
import RCLayout from "@/pages/rc/RCLayout";
import ClienteCollegati from "@/components/ClienteCollegati";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { FileText, Send, Cloud, Copy, TerminalSquare, Pencil, Sparkles, Save, Loader2, Download } from "lucide-react";
import { toast } from "sonner";

const LABELS = { rct: "RCT", rco: "RCO", organi_sociali: "Organi Sociali", tutela_legale: "Tutela Legale" };

export default function RCQuoteDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [quote, setQuote] = useState(null);
  const [audit, setAudit] = useState(null);
  const [email, setEmail] = useState("");
  const [emailMsg, setEmailMsg] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get(`/quotes/${id}`).then((r) => { setQuote(r.data); setEmail(r.data.organization?.email || ""); });
    api.get(`/quotes/${id}/audit`).then((r) => setAudit(r.data)).catch(() => {});
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (!quote) return <RCLayout><div className="text-stone-400 font-mono text-sm">Caricamento…</div></RCLayout>;
  const prem = quote.premium_result;
  const inp = quote.input_payload || {};

  const doPdf = async () => {
    try { await openPdf(`/quotes/${id}/pdf`, `preventivo_${quote.quote_number}.pdf`); toast.success("PDF generato e scaricato."); }
    catch (e) { toast.error("PDF non disponibile"); }
  };
  const doEmail = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/quotes/${id}/email`, { recipient: email, message: emailMsg });
      toast.success(data.note || "Email inviata.");
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const doCrm = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/quotes/${id}/crm-sync`);
      toast.success(`${data.note} (ID: ${data.external_id})`);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const doDuplicate = async () => {
    try {
      const { data } = await api.post(`/quotes/${id}/duplicate`);
      toast.success("Preventivo duplicato.");
      navigate(`/quotatore-rc/preventivi/${data.id}`);
    } catch (e) { toast.error("Errore duplicazione"); }
  };

  return (
    <RCLayout>
      <div data-testid="quote-detail-page">
        <button onClick={() => navigate("/quotatore-rc/preventivi")} className="text-xs text-stone-500 hover:text-stone-900 mb-3">← Storico</button>
        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="font-display text-3xl font-semibold tracking-tight text-stone-900 font-mono">{quote.quote_number}</h1>
              <OutcomeBadge status={quote.calc_status} size="lg" />
            </div>
            <p className="text-stone-500 text-sm mt-1">
              {quote.organization?.ragione_sociale || "Ente non collegato"} · Versione {quote.product_version} · Operatore {quote.operator_name}
            </p>
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex flex-wrap gap-2 mb-6">
          <Button data-testid="edit-btn" onClick={() => navigate(`/quotatore-rc/preventivi/${id}/modifica`)} className="rounded-sm bg-stone-900 hover:bg-stone-800">
            <Pencil className="w-4 h-4 mr-1.5" strokeWidth={1.5} /> Modifica
          </Button>
          <Button data-testid="pdf-btn" onClick={doPdf} variant="outline" className="rounded-sm">
            <FileText className="w-4 h-4 mr-1.5" strokeWidth={1.5} /> Genera PDF
          </Button>

          <Dialog>
            <DialogTrigger asChild>
              <Button data-testid="email-btn" variant="outline" className="rounded-sm">
                <Send className="w-4 h-4 mr-1.5" strokeWidth={1.5} /> Invia Email
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Invia preventivo via email</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label className="text-xs uppercase tracking-widest text-stone-500">Destinatario</Label>
                  <Input data-testid="email-recipient" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5 rounded-sm" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-stone-500">Messaggio</Label>
                  <Textarea data-testid="email-message" value={emailMsg} onChange={(e) => setEmailMsg(e.target.value)} className="mt-1.5 rounded-sm" rows={4}
                    placeholder="Gentile cliente, in allegato il preventivo richiesto…" />
                </div>
                <p className="text-xs text-amber-700">Invio simulato (mock). Configurare un provider reale per l'invio effettivo.</p>
              </div>
              <DialogFooter>
                <Button data-testid="email-send-confirm" onClick={doEmail} disabled={busy} className="rounded-sm bg-stone-900 hover:bg-stone-800">Invia</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <Button data-testid="crm-btn" onClick={doCrm} disabled={busy} variant="outline" className="rounded-sm">
            <Cloud className="w-4 h-4 mr-1.5" strokeWidth={1.5} /> Invia a CRM
          </Button>
          <Button data-testid="duplicate-btn" onClick={doDuplicate} variant="outline" className="rounded-sm">
            <Copy className="w-4 h-4 mr-1.5" strokeWidth={1.5} /> Duplica
          </Button>
        </div>

        {quote.crm_sync_status && quote.crm_sync_status !== "non_sincronizzato" && (
          <div data-testid="crm-status" className="mb-6 -mt-2 text-xs text-stone-500 bg-stone-100 border border-stone-200 rounded-sm px-3 py-2 inline-flex items-center gap-2">
            <Cloud className="w-3.5 h-3.5" strokeWidth={1.5} />
            Stato CRM: <span className="font-medium text-stone-700">{quote.crm_sync_status}</span>
            <span className="text-stone-400">· integrazione simulata (placeholder finché non si configura il CRM target)</span>
          </div>
        )}

        <div className="mb-6">
          <ClienteCollegati currentModule="rc" currentId={id}
            anagraficaId={quote.organization_id} nome={quote.organization?.ragione_sociale} piva={quote.organization?.partita_iva} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Premium + summary */}
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white border border-stone-200 rounded-sm shadow-sm p-5">
              <h2 className="font-display text-lg font-medium mb-4">Riepilogo configurazione</h2>
              <dl className="grid grid-cols-2 gap-y-3 text-sm">
                <Row label="Sezione" value={inp.section_code} />
                <Row label="Parametro" value={`${inp.parameter_code} = ${inp.parameter_value}`} />
                <Row label="Massimale" value={eur(inp.massimale).replace(",00", "")} />
                <Row label="Sconto" value={`${inp.sconto_pct || 0}%`} />
                <Row label="Aumento" value={`${inp.aumento_pct || 0}%`} />
              </dl>
            </div>

            {prem && (
              <div className="bg-white border border-stone-200 rounded-sm shadow-sm overflow-hidden">
                <div className="px-5 py-4 border-b border-stone-200"><h2 className="font-display text-lg font-medium">Dettaglio premio</h2></div>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-stone-100 text-stone-500 text-xs uppercase tracking-wide">
                      <th className="text-left font-medium px-5 py-2">Garanzia</th>
                      <th className="text-right font-medium px-5 py-2">Imponibile</th>
                      <th className="text-right font-medium px-5 py-2">Imposte</th>
                      <th className="text-right font-medium px-5 py-2">Lordo</th>
                    </tr>
                  </thead>
                  <tbody className="font-mono">
                    {Object.entries(prem.breakdown).map(([k, v]) => (
                      <tr key={k} className="border-b border-stone-100">
                        <td className="px-5 py-2.5 font-sans text-stone-700">{LABELS[k] || k}</td>
                        <td className="px-5 py-2.5 text-right">{eur(v.net)}</td>
                        <td className="px-5 py-2.5 text-right text-stone-400">{eur(v.tax)}</td>
                        <td className="px-5 py-2.5 text-right">{eur(v.gross)}</td>
                      </tr>
                    ))}
                    <tr className="bg-stone-50 font-semibold">
                      <td className="px-5 py-2.5 font-sans">TOTALE LORDO</td>
                      <td className="px-5 py-2.5 text-right">{eur(prem.net_total)}</td>
                      <td className="px-5 py-2.5 text-right text-stone-400">{eur(prem.tax_total)}</td>
                      <td className="px-5 py-2.5 text-right" data-testid="detail-gross-total">{eur(prem.gross_total)}</td>
                    </tr>
                  </tbody>
                </table>
                {prem.minimum_premium_applied && (
                  <div data-testid="min-premium-badge" className="px-5 py-2.5 bg-amber-100 border-t border-amber-300 flex items-center gap-2">
                    <span className="inline-block w-2 h-2 rounded-full bg-amber-500" />
                    <span className="text-xs font-semibold uppercase tracking-wide text-amber-900">Premio Minimo da Tariffa applicato</span>
                    <span className="text-xs text-amber-800 ml-auto font-mono">{eur(prem.minimum_premium_value)}</span>
                  </div>
                )}
              </div>
            )}

            {(quote.eligibility_result?.warnings?.length > 0 || quote.eligibility_result?.blocking_errors?.length > 0) && (
              <div className="space-y-2">
                {quote.eligibility_result.blocking_errors.map((e, i) => (
                  <p key={`blk-${i}-${e.slice(0, 12)}`} className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-sm px-3 py-2">{e}</p>
                ))}
                {quote.eligibility_result.warnings.map((e, i) => (
                  <p key={`wrn-${i}-${e.slice(0, 12)}`} className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-sm px-3 py-2">{e}</p>
                ))}
              </div>
            )}

            <RelazioneCard quoteId={id} quoteNumber={quote.quote_number} />
          </div>

          {/* Audit */}
          <div>
            <div className="bg-stone-50 border border-stone-200 rounded-sm p-4 sticky top-8" data-testid="audit-box">
              <div className="flex items-center gap-2 mb-3 text-stone-500">
                <TerminalSquare className="w-4 h-4" strokeWidth={1.5} />
                <span className="text-xs uppercase tracking-widest">Audit del calcolo</span>
              </div>
              {!audit && <p className="text-xs text-stone-400 audit-console">Audit non disponibile (bozza).</p>}
              {audit && (
                <div className="audit-console text-stone-700 space-y-2">
                  <div className="text-stone-400">› versione {audit.product_version}</div>
                  {(audit.all_steps || []).map((r, i) => (
                    <div key={`${r.rule}-${i}`} className="border-l-2 border-stone-300 pl-2">
                      <div className="text-stone-500">[{r.rule}] {r.label || r.msg}</div>
                      {r.formula && <div className="text-stone-800">{r.formula}</div>}
                      {r.bracket && <div className="text-stone-400">scaglione {r.bracket} · mass. {r.massimale}</div>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </RCLayout>
  );
}

function Row({ label, value }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-widest text-stone-400">{label}</dt>
      <dd className="text-stone-800 mt-0.5">{value}</dd>
    </div>
  );
}

function RelazioneCard({ quoteId, quoteNumber }) {
  const [sito, setSito] = useState("");
  const [framework, setFramework] = useState("");
  const [testo, setTesto] = useState("");
  const [showFramework, setShowFramework] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/quotes/${quoteId}/relazione`).then((r) => {
      setSito(r.data.sito_web || "");
      setFramework(r.data.framework || "");
      setTesto(r.data.relazione_testo || "");
    }).catch(() => {});
  }, [quoteId]);

  const doGenerate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post(`/quotes/${quoteId}/relazione/generate`, { sito_web: sito, framework });
      setTesto(data.relazione_testo || "");
      toast.success("Relazione generata con l'AI.");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setGenerating(false); }
  };
  const doSave = async () => {
    setSaving(true);
    try {
      await api.put(`/quotes/${quoteId}/relazione`, { relazione_testo: testo, sito_web: sito, framework });
      toast.success("Relazione salvata.");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };
  const doPdf = async () => {
    try { await openPdf(`/quotes/${quoteId}/relazione/pdf`, `relazione_${quoteNumber}.pdf`); }
    catch (e) { toast.error("Salva o genera la relazione prima di esportare il PDF."); }
  };

  return (
    <div data-testid="relazione-card" className="bg-white border border-stone-200 rounded-sm shadow-sm p-5">
      <div className="flex items-center gap-2 mb-1">
        <Sparkles className="w-4 h-4 text-stone-700" strokeWidth={1.5} />
        <h2 className="font-display text-lg font-medium">Relazione tecnica (AI)</h2>
      </div>
      <p className="text-xs text-stone-500 mb-4">
        Inserisci il sito del cliente: l'AI raccoglie le informazioni e compila la relazione seguendo la traccia. Puoi modificare la traccia e il testo prima di salvare.
      </p>

      <div className="space-y-1.5 mb-3">
        <Label className="text-xs uppercase tracking-widest text-stone-500">Sito web del cliente</Label>
        <Input data-testid="relazione-sito" value={sito} onChange={(e) => setSito(e.target.value)}
          placeholder="https://www.esempio.it" className="rounded-sm" />
      </div>

      <button data-testid="relazione-toggle-framework" onClick={() => setShowFramework((v) => !v)}
        className="text-xs text-stone-500 hover:text-stone-900 mb-2">
        {showFramework ? "▾ Nascondi traccia (framework)" : "▸ Personalizza traccia (framework)"}
      </button>
      {showFramework && (
        <Textarea data-testid="relazione-framework" value={framework} onChange={(e) => setFramework(e.target.value)}
          rows={10} className="rounded-sm font-mono text-xs mb-3" />
      )}

      <div className="flex flex-wrap gap-2 mb-4">
        <Button data-testid="relazione-generate-btn" onClick={doGenerate} disabled={generating}
          className="rounded-sm bg-stone-900 hover:bg-stone-800">
          {generating ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1.5" strokeWidth={1.5} />}
          {generating ? "Generazione…" : (testo ? "Rigenera con AI" : "Genera con AI")}
        </Button>
      </div>

      <div className="space-y-1.5">
        <Label className="text-xs uppercase tracking-widest text-stone-500">Testo relazione</Label>
        <Textarea data-testid="relazione-testo" value={testo} onChange={(e) => setTesto(e.target.value)}
          rows={16} placeholder="La relazione generata comparirà qui e potrà essere modificata prima di salvare."
          className="rounded-sm text-sm leading-relaxed" />
      </div>

      <div className="flex flex-wrap gap-2 mt-3">
        <Button data-testid="relazione-save-btn" onClick={doSave} disabled={saving || !testo} variant="outline" className="rounded-sm">
          {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" strokeWidth={1.5} />}
          Salva
        </Button>
        <Button data-testid="relazione-pdf-btn" onClick={doPdf} disabled={!testo} variant="outline" className="rounded-sm">
          <Download className="w-4 h-4 mr-1.5" strokeWidth={1.5} /> Scarica PDF
        </Button>
      </div>
    </div>
  );
}
